import { useEffect, useRef, useState } from 'react';
import {
  ACCESS_RESOURCE_TYPE_LABELS,
  type AccessResourceType,
} from '@infras/shared-types';
import { Icon } from '../Icon';
import {
  FILTER_KEY_LABELS,
  FILTER_KEY_VALUES,
  addToken,
  parseFilter,
  removeToken,
  serializeFilter,
  tokenOpLabel,
  tokenValueLabel,
  type FilterKey,
  type FilterToken,
} from '../../lib/requestChips';
import type { FilterLanguage, Token } from '../../lib/requestFilter';

/**
 * The /access filter language, adapted to the generic `FilterLanguage` shape. It
 * calls the SAME requestChips functions the bar always called, so the access list
 * behaves exactly as before; other lists (/tasks) pass their own.
 */
const asAccess = (t: Token) => t as FilterToken;
export const ACCESS_FILTER_LANG: FilterLanguage = {
  parse: parseFilter,
  serialize: (f) => serializeFilter({ tokens: f.tokens.map(asAccess), text: f.text }),
  add: (q, t) => addToken(q, asAccess(t)),
  remove: (q, t) => removeToken(q, asAccess(t)),
  keyLabel: (k) => FILTER_KEY_LABELS[k as FilterKey] ?? k,
  valueLabel: (t) => tokenValueLabel(asAccess(t)),
  opLabel: (t) => tokenOpLabel(asAccess(t)),
  menuKeys: ['status', 'system', 'risk', 'requester', 'assignee', 'expires'],
  values: (k) => FILTER_KEY_VALUES[k as FilterKey] ?? [],
  optionLabel: (k, v) => optionLabel(k as FilterKey, v),
  placeholder: 'Search or filter results… (e.g. status:pending)',
};

/**
 * Token filter bar for the /access list. The whole filter is one string (`q`,
 * held in the url by the page): `key:value` words render as removable tokens,
 * the rest is free text.
 *
 * Keyboard: type `status:pending` (or any `key:value`) and press Enter to turn it
 * into a token; Backspace in an empty input removes the last token; Escape
 * clears what you typed; every token's × is a real button. Plain free text (no
 * `:`) filters live as you type.
 */
export function FilterTokenBar({
  q,
  onChange,
  lang = ACCESS_FILTER_LANG,
}: {
  q: string;
  onChange: (q: string) => void;
  /** The token vocabulary. Defaults to the /access language. */
  lang?: FilterLanguage;
}) {
  const { parse: parseFilter, serialize: serializeFilter, add: addToken, remove: removeToken } = lang;
  const { tokens, text } = parseFilter(q);
  const [draft, setDraft] = useState(text);
  const inputRef = useRef<HTMLInputElement>(null);

  // Follow url changes (view switch, Clear, back button) unless the url already
  // agrees with what is typed — re-setting it would eat a trailing space mid-typing.
  useEffect(() => {
    setDraft((d) => (parseFilter(d).text === text && !d.includes(':') ? d : text));
  }, [text]);

  // Live free-text search: commit typed words that contain no `key:` shape.
  useEffect(() => {
    if (draft.includes(':')) return;
    if (draft.trim() === text) return;
    const t = setTimeout(() => onChange(serializeFilter({ tokens, text: draft })), 250);
    return () => clearTimeout(t);
  }, [draft, text, q]);

  const commit = () => {
    const typed = parseFilter(draft);
    let next = serializeFilter({ tokens, text: typed.text });
    for (const t of typed.tokens) next = addToken(next, t);
    if (next !== q) onChange(next);
    setDraft(typed.text);
  };

  const remove = (t: Token) => {
    onChange(removeToken(q, t));
    inputRef.current?.focus();
  };

  const hasAny = tokens.length > 0 || draft.trim() !== '';

  return (
    <div className="req-listtools">
      <div className="req-filterbar" data-testid="req-filterbar" role="search">
        <Icon name="search" className="req-ico" />
        {tokens.map((t) => {
          const invalid = lang.isValid ? !lang.isValid(t) : false;
          return (
          <span
            key={`${t.key}:${t.value}`}
            className={`req-token${invalid ? ' req-token--invalid' : ''}`}
            data-testid="req-token"
            data-token={`${t.key}:${t.value}`}
            data-invalid={invalid || undefined}
            title={invalid ? `“${t.value}” is not a ${lang.keyLabel(t.key).toLowerCase()} value — this filter is not applied` : undefined}
          >
            <span className="req-token__k">{lang.keyLabel(t.key)}</span>
            {lang.opLabel(t) && <span className="req-token__op">{lang.opLabel(t)}</span>}
            <span className="req-token__v">{lang.valueLabel(t)}</span>
            <button
              type="button"
              className="req-token__x"
              aria-label={`Remove filter ${lang.keyLabel(t.key)} ${lang.valueLabel(t)}`}
              title="Remove"
              onClick={() => remove(t)}
            >
              ×
            </button>
          </span>
          );
        })}
        <input
          ref={inputRef}
          className="req-filterbar__input"
          placeholder={tokens.length ? 'Search…' : lang.placeholder}
          aria-label="Filter tasks"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={commit}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              commit();
            } else if (e.key === 'Escape') {
              setDraft('');
              if (text) onChange(serializeFilter({ tokens, text: '' }));
            } else if (e.key === 'Backspace' && draft === '' && tokens.length) {
              e.preventDefault();
              onChange(removeToken(q, tokens[tokens.length - 1]));
            }
          }}
        />
        {hasAny && (
          <button
            type="button"
            className="mini-btn req-filterbar__clear"
            title="Clear all filters"
            onClick={() => {
              setDraft('');
              onChange('');
              inputRef.current?.focus();
            }}
          >
            Clear
          </button>
        )}
      </div>
      <AddFilterMenu
        lang={lang}
        onPick={(t) => {
          onChange(addToken(q, t));
          inputRef.current?.focus();
        }}
      />
    </div>
  );
}

function optionLabel(key: FilterKey, v: string): string {
  if (key === 'system') return ACCESS_RESOURCE_TYPE_LABELS[v as AccessResourceType] ?? v;
  if (key === 'requester' || key === 'assignee') return 'me';
  return tokenValueLabel({ key, value: v });
}

/** "Add filter" — every key/value pair as a button, grouped by key. Esc closes. */
function AddFilterMenu({ lang, onPick }: { lang: FilterLanguage; onPick: (t: Token) => void }) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);
  const btnRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (!wrapRef.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  const keys = lang.menuKeys;
  return (
    <div
      className="req-addfilter"
      ref={wrapRef}
      onKeyDown={(e) => {
        if (e.key === 'Escape' && open) {
          e.stopPropagation();
          setOpen(false);
          btnRef.current?.focus();
        }
      }}
    >
      <button
        ref={btnRef}
        type="button"
        className="btn"
        aria-haspopup="true"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
      >
        <Icon name="settings" size={13} className="req-ico" /> Add filter
      </button>
      {open && (
        <div className="req-addfilter__menu" role="group" aria-label="Add a filter">
          {keys.map((k) => (
            <div key={k} className="req-addfilter__group">
              <p className="req-addfilter__label">{lang.keyLabel(k)}</p>
              <div className="req-addfilter__opts">
                {lang.values(k).map((v, i) => (
                  <button
                    key={v}
                    type="button"
                    className="mini-btn"
                    // first option of the menu takes focus so the menu is reachable by keyboard
                    autoFocus={k === keys[0] && i === 0}
                    onClick={() => {
                      onPick({ key: k, value: v });
                      setOpen(false);
                    }}
                  >
                    {lang.optionLabel(k, v)}
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
